// src/components/common/ConfirmationModal.jsx
import React from 'react';
import { IoWarningOutline, IoCloseOutline } from 'react-icons/io5';

const ConfirmationModal = ({
  isOpen,
  onConfirm,
  onClose,
  onCancel,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'warning', // warning, danger, success, primary
  isLoading = false
}) => {
  // If onCancel is not provided, use onClose as a fallback
  const handleCancel = onCancel || onClose;

  if (!isOpen) return null;

  const variantClasses = {
    warning: {
      ring: 'ring-amber-500/20',
      iconWrap: 'bg-amber-50 text-amber-700 ring-1 ring-amber-100 dark:bg-amber-950/40 dark:text-amber-200 dark:ring-amber-900/40',
      button: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500/40',
      title: 'text-slate-900 dark:text-white',
      desc: 'text-slate-600 dark:text-slate-300',
    },
    danger: {
      ring: 'ring-red-500/20',
      iconWrap: 'bg-red-50 text-red-700 ring-1 ring-red-100 dark:bg-red-950/40 dark:text-red-200 dark:ring-red-900/40',
      button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500/40',
      title: 'text-slate-900 dark:text-white',
      desc: 'text-slate-600 dark:text-slate-300',
    },
    success: {
      ring: 'ring-emerald-500/20',
      iconWrap: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-900/40',
      button: 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500/40',
      title: 'text-slate-900 dark:text-white',
      desc: 'text-slate-600 dark:text-slate-300',
    },
    primary: {
      ring: 'ring-blue-500/20',
      iconWrap: 'bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40',
      button: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500/40',
      title: 'text-slate-900 dark:text-white',
      desc: 'text-slate-600 dark:text-slate-300',
    },
  };

  const v = variantClasses[confirmVariant] || variantClasses.warning;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={handleCancel}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className={`relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-xl ring-1 ${v.ring} dark:border-slate-800 dark:bg-slate-900`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div className="min-w-0">
            <h3 className={`truncate text-base font-semibold ${v.title}`}>
              {title}
            </h3>
            <p className={`mt-1 text-sm ${v.desc}`}>
              Please confirm to continue.
            </p>
          </div>

          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
            onClick={handleCancel}
            disabled={isLoading}
            aria-label="Close"
          >
            <IoCloseOutline className="text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5">
          <div className="flex gap-4">
            <div className={`mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${v.iconWrap}`}>
              <IoWarningOutline className="text-xl" />
            </div>

            <div className="min-w-0">
              {/* Keep message rendering flexible: string OR JSX */}
              {typeof message === 'string' ? (
                <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">
                  {message}
                </p>
              ) : (
                <div className="text-sm text-slate-700 dark:text-slate-200">
                  {message}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-5 py-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-end">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {cancelText}
          </button>

          <button
            type="button"
            className={`inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60 ${v.button}`}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
